// Rules for VALIDATION_CONSTRAINTS.md section 3 — node cardinality bounds.
// Boundary events are wired via parentNode, not sequence flow, so their
// incoming flow count is always 0 by construction. Recursive-flagged edges
// are excluded from counts to match the engine's `CanJoin = inputs - recursive`.
import { registerRule } from '../registry';
import { ValidationRule, ValidationIssue } from '../types';
import {
  buildLookup,
  getIncoming,
  getOutgoing,
  getFlowIncoming,
  getFlowOutgoing,
  getFamily,
  isForkGateway,
  isMergeGateway,
  labelOf,
} from '../helpers';

const rule_3_1_StartEventCardinality: ValidationRule = {
  id: '3.1',
  description: 'StartEvent must have 0 incoming and exactly 1 outgoing flow',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (getFamily(n.type) !== 'StartEvent') continue;
      const incoming = getFlowIncoming(lookup, n.id).length;
      const outgoing = getFlowOutgoing(lookup, n.id).length;
      if (incoming !== 0) {
        issues.push({
          severity: 'error',
          rule: '3.1',
          message: `${labelOf(n)} must have 0 incoming flows (found ${incoming}).`,
          nodeIds: [n.id],
        });
      }
      if (outgoing !== 1) {
        issues.push({
          severity: 'error',
          rule: '3.1',
          message: `${labelOf(n)} must have exactly 1 outgoing flow (found ${outgoing}).`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_3_1_StartEventCardinality);

const rule_3_2_EndEventCardinality: ValidationRule = {
  id: '3.2',
  description: 'EndEvent must have exactly 1 incoming and 0 outgoing flows',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (getFamily(n.type) !== 'EndEvent') continue;
      const incoming = getFlowIncoming(lookup, n.id).length;
      const outgoing = getFlowOutgoing(lookup, n.id).length;
      if (incoming !== 1) {
        issues.push({
          severity: 'error',
          rule: '3.2',
          message: `${labelOf(n)} must have exactly 1 incoming flow (found ${incoming}).`,
          nodeIds: [n.id],
        });
      }
      if (outgoing !== 0) {
        issues.push({
          severity: 'error',
          rule: '3.2',
          message: `${labelOf(n)} must have 0 outgoing flows (found ${outgoing}).`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_3_2_EndEventCardinality);

const rule_3_3_ActivityCardinality: ValidationRule = {
  id: '3.3',
  description: 'Activity must have exactly 1 incoming and 1 outgoing flow',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (getFamily(n.type) !== 'Activity') continue;
      // Recursive edges DO count toward activity cardinality — a ScriptTask
      // looping back via a recursive-flagged edge still has exactly one
      // outgoing flow. The recursive filter only matters for merge-gateway
      // CanJoin semantics (rule 3.8).
      const incoming = getIncoming(lookup, n.id).length;
      const outgoing = getOutgoing(lookup, n.id).length;
      if (incoming !== 1) {
        issues.push({
          severity: 'error',
          rule: '3.3',
          message: `${labelOf(n)} must have exactly 1 incoming flow (found ${incoming}).`,
          nodeIds: [n.id],
        });
      }
      if (outgoing !== 1) {
        issues.push({
          severity: 'error',
          rule: '3.3',
          message: `${labelOf(n)} must have exactly 1 outgoing flow (found ${outgoing}).`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_3_3_ActivityCardinality);

const rule_3_4_IntermediateCatchingCardinality: ValidationRule = {
  id: '3.4',
  description: 'IntermediateCatchingEvent must have exactly 1 incoming and 1 outgoing flow',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (getFamily(n.type) !== 'IntermediateCatching') continue;
      const incoming = getFlowIncoming(lookup, n.id).length;
      const outgoing = getFlowOutgoing(lookup, n.id).length;
      if (incoming !== 1) {
        issues.push({
          severity: 'error',
          rule: '3.4',
          message: `${labelOf(n)} must have exactly 1 incoming flow (found ${incoming}).`,
          nodeIds: [n.id],
        });
      }
      if (outgoing !== 1) {
        issues.push({
          severity: 'error',
          rule: '3.4',
          message: `${labelOf(n)} must have exactly 1 outgoing flow (found ${outgoing}).`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_3_4_IntermediateCatchingCardinality);

const rule_3_5_IntermediateThrowCardinality: ValidationRule = {
  id: '3.5',
  description: 'IntermediateThrow event must have exactly 1 incoming and 1 outgoing flow',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (getFamily(n.type) !== 'IntermediateThrow') continue;
      const incoming = getFlowIncoming(lookup, n.id).length;
      const outgoing = getFlowOutgoing(lookup, n.id).length;
      if (incoming !== 1) {
        issues.push({
          severity: 'error',
          rule: '3.5',
          message: `${labelOf(n)} must have exactly 1 incoming flow (found ${incoming}).`,
          nodeIds: [n.id],
        });
      }
      if (outgoing !== 1) {
        issues.push({
          severity: 'error',
          rule: '3.5',
          message: `${labelOf(n)} must have exactly 1 outgoing flow (found ${outgoing}).`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_3_5_IntermediateThrowCardinality);

const rule_3_6_BoundaryEventCardinality: ValidationRule = {
  id: '3.6',
  description: 'BoundaryEvent must have 0 incoming and exactly 1 outgoing flow',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (getFamily(n.type) !== 'BoundaryEvent') continue;
      // Boundary events attach to activities via parentNode; sequence-flow
      // incoming is always invalid regardless of trigger type.
      const incoming = getFlowIncoming(lookup, n.id).length;
      const outgoing = getFlowOutgoing(lookup, n.id).length;
      if (incoming !== 0) {
        issues.push({
          severity: 'error',
          rule: '3.6',
          message: `${labelOf(n)} must have 0 incoming flows (found ${incoming}).`,
          nodeIds: [n.id],
        });
      }
      if (outgoing !== 1) {
        issues.push({
          severity: 'error',
          rule: '3.6',
          message: `${labelOf(n)} must have exactly 1 outgoing flow (found ${outgoing}).`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_3_6_BoundaryEventCardinality);

const rule_3_7_GatewayForkCardinality: ValidationRule = {
  id: '3.7',
  description: 'Fork gateway (IsMerging=false) must have exactly 1 incoming and at least 1 outgoing flow',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (!isForkGateway(n)) continue;
      const incoming = getFlowIncoming(lookup, n.id).length;
      const outgoing = getFlowOutgoing(lookup, n.id).length;
      if (incoming !== 1) {
        issues.push({
          severity: 'error',
          rule: '3.7',
          message: `${labelOf(n)} must have exactly 1 incoming flow (found ${incoming}).`,
          nodeIds: [n.id],
        });
      }
      if (outgoing < 1) {
        issues.push({
          severity: 'error',
          rule: '3.7',
          message: `${labelOf(n)} must have at least 1 outgoing flow (found ${outgoing}).`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_3_7_GatewayForkCardinality);

const rule_3_8_GatewayMergeCardinality: ValidationRule = {
  id: '3.8',
  description: 'Merge gateway (IsMerging=true) must have at least 1 incoming and exactly 1 outgoing flow',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (!isMergeGateway(n)) continue;
      const incoming = getFlowIncoming(lookup, n.id).length;
      const outgoing = getFlowOutgoing(lookup, n.id).length;
      if (incoming < 1) {
        issues.push({
          severity: 'error',
          rule: '3.8',
          message: `${labelOf(n)} must have at least 1 incoming flow (found ${incoming}).`,
          nodeIds: [n.id],
        });
      }
      if (outgoing !== 1) {
        issues.push({
          severity: 'error',
          rule: '3.8',
          message: `${labelOf(n)} must have exactly 1 outgoing flow (found ${outgoing}).`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_3_8_GatewayMergeCardinality);
